import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchGenerationStatus,
  GenerationStatus,
  RenderedResource,
} from 'api/video';

export interface UseVideoPollingOptions {
  generationId: number | null;
  onCompleted: (resource: RenderedResource | null) => void;
  onError: (message: string) => void;
  /**
   * Expected render time in ms — used to simulate progress and to derive
   * the polling ceiling (`× 2.5` by default). Default 90 000 covers T1
   * Creatomate renders, which empirically run 60–120 s end-to-end
   * (observed ~117 s on 2026-04-18 QA).
   */
  expectedDurationMs?: number;
  /**
   * Hard ceiling in ms after which polling gives up. Default: expectedDurationMs * 2.5.
   * Prevents indefinite polling when the webhook never fires.
   *
   * Prefer passing this explicitly. `expectedDurationMs` drives the progress
   * bar, so leaving the ceiling derived from it lets a display estimate decide
   * when we stop waiting. That is exactly how SYM-5858 happened: an unmeasured
   * "~60-90s" for Hailuo became a 3 min 7.5 s deadline on a 6 min render, and
   * every successful generation was reported to the customer as a failure.
   */
  maxDurationMs?: number;
  /**
   * Called instead of `onError` when the ceiling is reached.
   *
   * Reaching the ceiling is not a generation failure. The work is server-side
   * and webhook-driven, so it is still running and will still complete and
   * attach itself to the ad. Supply this to say that, rather than telling the
   * customer their clip could not be generated. When omitted the ceiling falls
   * back to `onError`, which is the older behaviour.
   */
  onTimeout?: () => void;
  /**
   * When true, skips real API calls and simulates completion after
   * expectedDurationMs. Used for Track 2 (fal.ai) which is not yet on QA.
   */
  isMock?: boolean;
}

export interface UseVideoPollingReturn {
  status: GenerationStatus | null;
  /** 0–100, simulated based on elapsed time + terminal state */
  progress: number;
  isPolling: boolean;
}

const TERMINAL_STATES: GenerationStatus[] = [
  'completed',
  'partial_complete',
  'failed',
  'blocked',
];
const POLL_INTERVAL_MS = 2500;
const PROGRESS_TICK_MS = 200;
/**
 * How often to check once the customer is no longer being made to wait.
 *
 * SYM-6075. Past the ceiling nobody is watching a progress bar, so the check
 * can be slower than the 2.5 s of the wait, but not by much: the customer was
 * told the clip is added as soon as it is ready, and a minute between the
 * clip landing and the page showing it read as "not ready" in testing
 * (2 Sep 2026). Five seconds is unnoticeable to a person and the cost is
 * bounded: only the tab that asked for the clip checks, and only until the
 * clip lands, the generation fails, or the watchdog fails it.
 *
 * Exported so the test pins the real number rather than a copy of it.
 */
export const SLOW_POLL_INTERVAL_MS = 5_000;

export function useVideoPolling({
  generationId,
  onCompleted,
  onError,
  expectedDurationMs = 90_000,
  maxDurationMs,
  onTimeout,
  isMock = false,
}: UseVideoPollingOptions): UseVideoPollingReturn {
  const [status, setStatus] = useState<GenerationStatus | null>(null);
  const [progress, setProgress] = useState(0);
  const [isPolling, setIsPolling] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeRef = useRef<number>(0);
  const completedRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (progressRef.current) clearInterval(progressRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    pollRef.current = null;
    progressRef.current = null;
    timeoutRef.current = null;
  }, []);

  const stopPolling = useCallback(
    (finalProgress?: number) => {
      clearTimers();
      setIsPolling(false);
      if (finalProgress !== undefined) setProgress(finalProgress);
    },
    [clearTimers]
  );

  useEffect(() => {
    if (!generationId) return;

    completedRef.current = false;
    setStatus(null);
    setProgress(5);
    setIsPolling(true);
    startTimeRef.current = Date.now();

    // Smooth progress simulation: 5% → 85% over expectedDurationMs
    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const simulated = Math.min(85, 5 + (elapsed / expectedDurationMs) * 80);
      setProgress(simulated);
    }, PROGRESS_TICK_MS);

    const poll = async () => {
      if (completedRef.current) return;
      try {
        const result = await fetchGenerationStatus(generationId);
        // SYM-6367. Asked again AFTER the wait: when the server is slow, several
        // checks are in flight at once and all come back "completed" together.
        // QA, 30 Sep 2026, job 25881: 14 checks answered within two seconds and
        // the finished video was announced three times, so it showed three times.
        if (completedRef.current) return;
        setStatus(result.status);

        if (
          result.status === 'completed' ||
          result.status === 'partial_complete'
        ) {
          completedRef.current = true;
          stopPolling(100);
          onCompleted(result.rendered_resource);
        } else if (result.status === 'failed' || result.status === 'blocked') {
          completedRef.current = true;
          stopPolling();
          onError(result.error_message ?? 'Video generation failed.');
        }
      } catch {
        // transient network error — keep polling
      }
    };

    // Hard ceiling — the point where we stop making the customer wait.
    // Reaching it is not a failure: the generation is server-side and carries
    // on regardless. Callers that pass onTimeout get to say so.
    const ceiling = maxDurationMs ?? expectedDurationMs * 2.5;
    timeoutRef.current = setTimeout(() => {
      if (completedRef.current) return;

      // Without onTimeout this is the older behaviour: give up and report it.
      if (!onTimeout) {
        completedRef.current = true;
        clearTimers();
        setIsPolling(false);
        onError('Video generation timed out. Please try again.');
        return;
      }

      // SYM-6075. The deadline ends the WAIT, not the WORK — so it must not end
      // the watch. Stopping outright left the modal saying the clip was still
      // being made while the finished clip sat on the server, and nothing
      // brought it in short of reloading the page, which is the opposite of
      // what that screen promises. Drop the progress bar and keep checking, far
      // more slowly: the cost is one request a minute, and what it buys is the
      // clip arriving on its own, as the customer was told it would.
      if (progressRef.current) clearInterval(progressRef.current);
      progressRef.current = null;
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(poll, SLOW_POLL_INTERVAL_MS);

      onTimeout();
    }, ceiling);

    if (isMock) {
      const mockTimer = setTimeout(() => {
        if (completedRef.current) return;
        completedRef.current = true;
        if (progressRef.current) clearInterval(progressRef.current);
        progressRef.current = null;
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
        setIsPolling(false);
        setProgress(100);
        onCompleted(null);
      }, expectedDurationMs);

      return () => {
        completedRef.current = true;
        clearTimeout(mockTimer);
        clearTimers();
        setIsPolling(false);
      };
    }

    poll(); // immediate first check
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      completedRef.current = true;
      clearTimers();
      setIsPolling(false);
    };
  }, [generationId]); // eslint-disable-line react-hooks/exhaustive-deps

  return { status, progress, isPolling };
}
